import type { ParaScoreButton } from './lms-header-supplement.util';

export const LMS_STORY_HIDDEN_ACC_TEXTS = ['Door.png', 'Vector.svg'] as const;

export function hideStoryHiddenAccTextElements(doc: Document, accTexts: readonly string[] = LMS_STORY_HIDDEN_ACC_TEXTS): void {
	for (const accText of accTexts) {
		doc.querySelectorAll(`[data-acc-text="${accText}"]`).forEach((el) => {
			const node = el as HTMLElement;
			node.style.setProperty('display', 'none', 'important');
			node.style.setProperty('visibility', 'hidden', 'important');
			node.style.setProperty('pointer-events', 'none', 'important');
		});
	}
}

/** Auto-select "Start over" when Storyline PlayerMemory shows the resume prompt. */
export function dismissStoryResumePrompt(doc: Document): void {
	const prompt = doc.querySelector('.cs-ResumePromptSlide');
	if (!prompt) {
		return;
	}
	const startOverBtn =
		prompt.querySelector('[class*="ResumePromptSlide-btn1"]') ??
		Array.from(prompt.querySelectorAll('[role="button"], [tabindex="0"], button, [data-acc-text]')).find((el) => {
			const label = `${el.textContent || ''} ${el.getAttribute('data-acc-text') || ''} ${el.getAttribute('aria-label') || ''}`.toLowerCase();
			return label.includes('start over') || label.includes('start from') || label.includes('no');
		});
	if (startOverBtn instanceof HTMLElement) {
		startOverBtn.click();
	}
}

export function buildStoryHiddenAccTextCss(accTexts: readonly string[] = LMS_STORY_HIDDEN_ACC_TEXTS): string {
	return accTexts
		.map(
			(accText) => `[data-acc-text="${accText}"] {
						display: none !important;
						visibility: hidden !important;
						pointer-events: none !important;
					}`
		)
		.join('\n');
}

/** Storyline hides inactive layers with CSS — keep filtering those out. */
export function isStoryElementHidden(el: Element): boolean {
	const node = el as HTMLElement;
	const rects = node.getClientRects();
	if (rects.length === 0) {
		return true;
	}
	const rect = rects[0];
	if (rect.width <= 0 || rect.height <= 0) {
		return true;
	}
	const win = node.ownerDocument?.defaultView;
	if (!win) {
		return true;
	}
	const style = win.getComputedStyle(node);
	return style.display === 'none' || style.visibility === 'hidden' || Number(style.opacity) <= 0.05;
}

/** Any overlap with the iframe viewport (not “fully inside with margin”). */
export function storyControlIntersectsViewport(rect: DOMRect, viewW: number, viewH: number): boolean {
	if (viewW <= 0 || viewH <= 0) {
		return false;
	}
	return rect.bottom > 0 && rect.right > 0 && rect.top < viewH && rect.left < viewW;
}

/** NEXT / player controls: hidden-by-CSS check plus loose viewport overlap. */
export function isStoryControlVisible(el: Element): boolean {
	if (isStoryElementHidden(el)) {
		return false;
	}
	const node = el as HTMLElement;
	const rect = node.getClientRects()[0];
	const docEl = node.ownerDocument?.documentElement;
	if (!docEl) {
		return false;
	}
	return storyControlIntersectsViewport(rect, docEl.clientWidth, docEl.clientHeight);
}

export function isStoryPlayerChrome(el: Element): boolean {
	const node = el as HTMLElement;
	if (
		node.closest(
			'#bottom_bar, #bottom-bar, #nav-controls, .slide-bottom, .cs-bottombar, .cs-panel, [class*="bottombar"], [class*="player-tab"]'
		)
	) {
		return true;
	}
	const doc = node.ownerDocument;
	if (!doc) {
		return false;
	}
	const rect = node.getBoundingClientRect();
	const viewH = doc.documentElement.clientHeight || doc.body?.clientHeight || 0;
	return viewH > 0 && rect.top > viewH - 56;
}

/**
 * Storyline score buttons (1–4). Viewport/size gates are intentionally loose:
 * IO + layout probes decide which slide is active; strict clipping checks excluded
 * users on zoom, display scaling, or short iframe heights.
 */
export function isStoryScoreButton(el: Element): boolean {
	if (isStoryElementHidden(el) || isStoryPlayerChrome(el)) {
		return false;
	}
	const rect = el.getBoundingClientRect();
	return rect.width > 0 && rect.height > 0;
}

export function canAttachStoryScoreClickOverlay(source: HTMLElement): boolean {
	return isStoryScoreButton(source);
}

export function findStoryScoreButtonElement(doc: Document, score: ParaScoreButton): Element | null {
	const selectors = [`[data-model-id][data-acc-text="${score}"]`, `[data-acc-text="${score}"]`];
	let best: Element | null = null;
	let bestArea = 0;
	for (const selector of selectors) {
		for (const el of Array.from(doc.querySelectorAll(selector))) {
			if (!isStoryScoreButton(el)) {
				continue;
			}
			const rect = el.getBoundingClientRect();
			const area = rect.width * rect.height;
			if (area > bestArea) {
				bestArea = area;
				best = el;
			}
		}
	}
	return best;
}

export function findVisibleStoryNextControl(doc: Document): HTMLElement | null {
	const selectors = [
		'[data-acc-text="NEXT"]',
		'[data-acc-text="Next"]',
		'[data-acc-text="next"]',
		'[aria-label*="NEXT" i]',
		'[aria-label*="Next" i]',
	];
	for (const selector of selectors) {
		const match = Array.from(doc.querySelectorAll(selector)).find(
			(el) => isStoryControlVisible(el) && !isStoryPlayerChrome(el)
		);
		if (match) {
			return match as HTMLElement;
		}
	}
	const fallback = Array.from(doc.querySelectorAll('[data-acc-text], [role="button"], button, [tabindex="0"]')).find((el) => {
		if (!isStoryControlVisible(el) || isStoryPlayerChrome(el)) {
			return false;
		}
		const label = `${el.textContent || ''} ${el.getAttribute('data-acc-text') || ''} ${el.getAttribute('aria-label') || ''}`.trim();
		return /\bNEXT\b/i.test(label);
	});
	return (fallback as HTMLElement | undefined) ?? null;
}

export function isStoryPlayerPrevInChrome(el: Element): boolean {
	if (!isStoryPlayerChrome(el)) {
		return false;
	}
	const node = el as HTMLElement;
	const rect = node.getBoundingClientRect();
	if (rect.width <= 0 || rect.height <= 0) {
		return false;
	}
	const style = node.ownerDocument?.defaultView?.getComputedStyle(node);
	return !!style && style.display !== 'none' && style.visibility !== 'hidden';
}

export function findStoryPlayerPrevControl(doc: Document): HTMLElement | null {
	const selectors = [
		'[data-acc-text="PREV"]',
		'[data-acc-text="Prev"]',
		'[data-acc-text="prev"]',
		'[aria-label*="PREV" i]',
		'[aria-label*="Previous" i]',
	];
	for (const selector of selectors) {
		const match = Array.from(doc.querySelectorAll(selector)).find((el) => isStoryPlayerPrevInChrome(el));
		if (match) {
			return match as HTMLElement;
		}
	}
	return (
		(Array.from(doc.querySelectorAll('[data-acc-text], [role="button"], button, [tabindex="0"]')).find((el) => {
			if (!isStoryPlayerPrevInChrome(el)) {
				return false;
			}
			const label = `${el.textContent || ''} ${el.getAttribute('data-acc-text') || ''} ${el.getAttribute('aria-label') || ''}`.trim();
			return /\bPREV\b/i.test(label) || /\bPrevious\b/i.test(label);
		}) as HTMLElement | undefined) ?? null
	);
}

export function resolveStoryPlayerPrevBridgeTarget(prevEl: HTMLElement): HTMLElement {
	return (prevEl.closest('[role="button"], button, [class*="prev" i], .cs-button') as HTMLElement | null) ?? prevEl;
}

export function isStoryPlayerPrevEnabledForNavigation(prevEl: HTMLElement): boolean {
	const btn = (prevEl.closest('[role="button"], button, [tabindex]') as HTMLElement | null) ?? prevEl;
	if (btn.matches(':disabled, [aria-disabled="true"]')) {
		return false;
	}
	if (btn.getAttribute('tabindex') === '-1') {
		return false;
	}
	const style = btn.ownerDocument?.defaultView?.getComputedStyle(btn);
	return !!style && style.pointerEvents !== 'none';
}
